import React, { useMemo } from "react";
import Select from "react-select";
import { CFormLabel, CInputGroup } from "@coreui/react";

/**
 * CoreUIReactSelect
 *
 * Wrapper de react-select que segue o look&feel do CoreUI/Bootstrap.
 * Agora suporta single e multi select de forma controlada via prop `isMulti`.
 *
 * Props principais:
 * - label: string | ReactNode
 * - options: Array<{ label: string; value: string | number }>
 * - isMulti?: boolean (default false)
 * - value: (isMulti === false) => string | number | null
 *           (isMulti === true)  => Array<string | number>
 * - onChange: (value, option) => void
 *      * single: (primitive | null, option | null)
 *      * multi:  (primitive[], option[])
 * - placeholder?: string
 * - disabled?: boolean
 * - clearable?: boolean (default true)
 * - size?: 'sm' | 'lg'
 * - className?: string
 * - menuPortalTarget?: HTMLElement (ex: document.body)
 */
export default function SearchSelect({
  label,
  options,
  isMulti = false,
  value, // single: string | number | null;  multi: Array<string | number>
  onChange, // (value, option) => void
  placeholder = "Selecione…",
  disabled,
  clearable = true,
  size, // 'sm' | 'lg'
  className,
  menuPortalTarget, // opcional: document.body para evitar clipping em modais
}) {
  // Converte valor(ores) primitivo(s) em option(s)
  const selected = useMemo(() => {
    if (!options) return isMulti ? [] : null;
    if (isMulti) {
      const values = Array.isArray(value) ? value : [];
      return options.filter((o) => values.includes(o.value));
    }
    return options.find((o) => o.value === value) ?? null;
  }, [options, value, isMulti]);

  // estilos inline usando variáveis do CoreUI/Bootstrap
  const styles = {
    control: (base, state) => ({
      ...base,
      minHeight: size === "sm" ? 31 : size === "lg" ? 45 : 38,
      borderRadius: "0.375rem", // .rounded
      backgroundColor: "var(--cui-body-bg)",
      borderColor: state.isFocused
        ? "var(--cui-primary)"
        : "var(--cui-border-color, #dee2e6)",
      boxShadow: state.isFocused
        ? "0 0 0 0.25rem rgba(var(--cui-primary-rgb, 13,110,253), .25)"
        : "none",
      ":hover": {
        borderColor: state.isFocused
          ? "var(--cui-primary)"
          : "var(--cui-border-color, #ced4da)",
      },
      fontSize: size === "sm" ? "0.875rem" : size === "lg" ? "1.1rem" : "1rem",
      lineHeight: 1.5,
      paddingLeft: 0,
    }),
    placeholder: (base) => ({
      ...base,
      color: "var(--cui-secondary-color, #6c757d)",
    }),
    singleValue: (base) => ({
      ...base,
      color: "var(--cui-body-color, #212529)",
    }),
    input: (base) => ({
      ...base,
      color: "var(--cui-body-color, #212529)",
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "0 0.5rem",
    }),
    indicatorsContainer: (base) => ({
      ...base,
      paddingRight: "0.25rem",
    }),
    clearIndicator: (base) => ({
      ...base,
      cursor: "pointer",
    }),
    dropdownIndicator: (base) => ({
      ...base,
      padding: "0.25rem 0.5rem",
    }),
    menu: (base) => ({
      ...base,
      borderRadius: "0.375rem",
      marginTop: 4,
      boxShadow:
        "0 .5rem 1rem rgba(0,0,0,.15), 0 .125rem .25rem rgba(0,0,0,.075)",
      zIndex: 5,
    }),
    menuPortal: (base) => ({ ...base, zIndex: 1056 }), // acima de modais
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "var(--cui-primary)"
        : state.isFocused
        ? "var(--cui-tertiary-bg, #f8f9fa)"
        : "transparent",
      color: state.isSelected ? "var(--cui-light, #fff)" : "inherit",
      cursor: state.isDisabled ? "not-allowed" : "pointer",
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: "var(--cui-tertiary-bg, #f1f3f5)",
      borderRadius: "0.375rem",
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: "var(--cui-body-color, #212529)",
      padding: "0.1rem 0.25rem",
    }),
    multiValueRemove: (base) => ({
      ...base,
      ":hover": { backgroundColor: "transparent", color: "var(--cui-danger)" },
    }),
  };

  // classes para aproximar das do CoreUI
  const classNames = {
    container: () => `w-100 ${className || ""}`,
    control: () =>
      `form-control p-0 ${size === "sm" ? "form-control-sm" : ""} ${
        size === "lg" ? "form-control-lg" : ""
      }`,
    menu: () => "dropdown-menu show p-0",
    option: (state) => `dropdown-item ${state.isSelected ? "active" : ""}`,
    valueContainer: () => "px-2",
    indicatorsContainer: () => "px-1",
  };

  // Normaliza onChange para devolver primitivos (ou array de primitivos) + opções completas
  const handleChange = (opt) => {
    if (isMulti) {
      const arr = Array.isArray(opt) ? opt : [];
      const values = arr.map((o) => o.value);
      onChange?.(values);
    } else {
      onChange?.(opt ? opt.value : null, opt || null);
    }
  };

  return (
    <CInputGroup
      className={`coreui-react-select ${size ? `input-group-${size}` : ""}`}
    >
      {label && <CFormLabel>{label}</CFormLabel>}
      <Select
        classNamePrefix="rs"
        options={options}
        isMulti={isMulti}
        value={selected}
        onChange={handleChange}
        isDisabled={disabled}
        isClearable={clearable}
        placeholder={placeholder}
        styles={styles}
        classNames={classNames}
        menuPortalTarget={menuPortalTarget}
        menuPosition="fixed"
        closeMenuOnSelect={!isMulti}
        // importante para acessibilidade
        ariaLiveMessages={{ onFocus: () => null, guidance: () => null }}
        // filtra apenas pelas opções existentes (react-select já faz isso)
        filterOption={undefined}
      />
    </CInputGroup>
  );
}
